import type { RouteRecordRaw } from 'vue-router'

import {
  ReviewsProductAssets,
  ReviewsProductAssetsEdit,
  ReviewsProductAssetsSubheader,
  ReviewsProductDataProvider,
  ReviewsProductDetails,
  ReviewsProductDetailsEdit,
  ReviewsProductDetailsSubheader,
  ReviewsProductEdit,
  ReviewsProductGlobalFeatures,
  ReviewsProductGlobalFeaturesEdit,
  ReviewsProductGlobalFeaturesSubheader,
  ReviewsProductPreferences,
  ReviewsProductPreferencesEdit,
  ReviewsProductPricing,
  ReviewsProductPricingEdit,
  ReviewsProductPricingSubheader,
  ReviewsProductResources,
  ReviewsProductReviewContainer,
  ReviewsProductCampaignsAndReviews,
  ReviewsProductsList,
  ReviewsProductView,
  ReviewsProductResourcesBadges,
  ReviewsProductResourcesWidgets,
  ReviewsProductCampaignOverview,
  ReviewsProductCampaignEdit,
  ReviewsProductCampaignNew,
  ReviewsProductCampaignPersonalizationNew,
  ReviewsProductCampaignPersonalizationEdit,
  ReviewsProductLeads,
  ReviewsProductLeadDetail,
  ReviewsProductTopProductsRanking,
  ReviewsProductInsightsHub,
  MarketInsightsDataProvider,
  CompetitionInsightsDataProvider,
  BuyerInsightsDataProvider,
  ReviewsInsightsDataProvider,
} from '@/components/ReviewsManagement'
import { FEATURE_FLAG } from '@/composables/featureFlagCatalog'
import { ReviewsInsightsPerspective, ReviewsProductRouteTabs, ReviewsProductSubPageRoute, ReviewsRouteName } from '@/router/types'
import { ReviewsDataProvider } from '@/views'

export const reviewsProductsRoutes: RouteRecordRaw = {
  path: '/reviews',
  component: ReviewsDataProvider,
  children: [
    {
      path: '',
      name: ReviewsRouteName.ReviewsProducts,
      redirect: { name: ReviewsRouteName.ReviewsProductsIndex },
    },
    {
      path: 'index',
      name: ReviewsRouteName.ReviewsProductsIndex,
      props: true,
      component: ReviewsProductsList,
    },
    {
      path: ':productId',
      props: true,
      component: ReviewsProductDataProvider,
      children: [
        {
          path: `${ReviewsProductSubPageRoute.ReviewsAndCampaigns}/:surveyResponseId`,
          name: ReviewsRouteName.ReviewsProductReviewDetails,
          props: true,
          component: ReviewsProductReviewContainer,
        },
        {
          path: `${ReviewsProductSubPageRoute.ReviewsAndCampaigns}/new`,
          name: ReviewsRouteName.ReviewsProductCampaignNew,
          props: true,
          component: ReviewsProductCampaignNew,
        },
        {
          path: `${ReviewsProductSubPageRoute.ReviewsAndCampaigns}/:campaignId/${ReviewsProductSubPageRoute.Overview}`,
          name: ReviewsRouteName.ReviewsProductCampaignOverview,
          props: true,
          component: ReviewsProductCampaignOverview,
        },
        {
          path: `${ReviewsProductSubPageRoute.ReviewsAndCampaigns}/:campaignId/edit`,
          name: ReviewsRouteName.ReviewsProductCampaignEdit,
          props: true,
          component: ReviewsProductCampaignEdit,
        },
        {
          path: `${ReviewsProductSubPageRoute.ReviewsAndCampaigns}/:campaignId/${ReviewsProductSubPageRoute.Personalisations}/new`,
          name: ReviewsRouteName.ReviewsProductCampaignPersonalizationNew,
          props: true,
          component: ReviewsProductCampaignPersonalizationNew,
        },
        {
          path: `${ReviewsProductSubPageRoute.ReviewsAndCampaigns}/:campaignId/${ReviewsProductSubPageRoute.Personalisations}/:personalizationId/edit`,
          name: ReviewsRouteName.ReviewsProductCampaignPersonalizationEdit,
          props: true,
          component: ReviewsProductCampaignPersonalizationEdit,
        },
        {
          path: ReviewsProductSubPageRoute.ReviewsAndCampaigns,
          name: ReviewsRouteName.ReviewsProductReviewsAndCampaigns,
          props: true,
          component: ReviewsProductCampaignsAndReviews,
        },
        {
          path: ReviewsProductSubPageRoute.Leads,
          name: ReviewsRouteName.ReviewsProductLeads,
          props: true,
          component: ReviewsProductLeads,
          meta: { featureFlag: FEATURE_FLAG.REVIEWS_PRODUCTS.LEADS_RELEASE },
        },
        {
          path: ReviewsProductSubPageRoute.TopProductsRanking,
          name: ReviewsRouteName.ReviewsProductTopProductsRanking,
          props: true,
          component: ReviewsProductTopProductsRanking,
          meta: { featureFlag: FEATURE_FLAG.REVIEWS_PRODUCTS.TOP_PRODUCTS_RANKING_RELEASE },
        },
        {
          path: ReviewsProductSubPageRoute.Insights,
          name: ReviewsRouteName.ReviewsProductInsights,
          props: true,
          component: ReviewsProductInsightsHub,
          meta: { featureFlag: FEATURE_FLAG.REVIEWS_PRODUCTS.INSIGHTS_HUB_RELEASE },
          redirect: { name: ReviewsRouteName.ReviewsProductInsightsMarket },
          children: [
            {
              path: ReviewsInsightsPerspective.Market,
              name: ReviewsRouteName.ReviewsProductInsightsMarket,
              component: MarketInsightsDataProvider,
            },
            {
              path: ReviewsInsightsPerspective.Competition,
              name: ReviewsRouteName.ReviewsProductInsightsCompetition,
              component: CompetitionInsightsDataProvider,
            },
            {
              path: ReviewsInsightsPerspective.Buyer,
              name: ReviewsRouteName.ReviewsProductInsightsBuyer,
              component: BuyerInsightsDataProvider,
            },
            {
              path: ReviewsInsightsPerspective.Reviews,
              name: ReviewsRouteName.ReviewsProductInsightsReviews,
              component: ReviewsInsightsDataProvider,
            },
          ],
        },
        {
          path: `${ReviewsProductSubPageRoute.Leads}/:leadId`,
          name: ReviewsRouteName.ReviewsProductLeadDetail,
          props: true,
          component: ReviewsProductLeadDetail,
          meta: { featureFlag: FEATURE_FLAG.REVIEWS_PRODUCTS.LEADS_RELEASE },
        },
        {
          path: ReviewsProductSubPageRoute.Resources,
          name: ReviewsRouteName.ReviewsProductResourcesView,
          props: true,
          component: ReviewsProductResources,
          meta: {
            tabId: ReviewsProductSubPageRoute.Widgets,
          },
          redirect: { name: ReviewsRouteName.ReviewsProductResourcesWidgetsView },
          children: [
            {
              path: ReviewsProductSubPageRoute.Widgets,
              name: ReviewsRouteName.ReviewsProductResourcesWidgetsView,
              props: true,
              component: ReviewsProductResourcesWidgets,
              meta: {
                tabId: ReviewsProductSubPageRoute.Widgets,
              },
            },
            {
              path: ReviewsProductSubPageRoute.Badges,
              name: ReviewsRouteName.ReviewsProductResourcesBadgesView,
              props: true,
              component: ReviewsProductResourcesBadges,
              meta: {
                tabId: ReviewsProductSubPageRoute.Badges,
              },
            },
          ],
        },
        {
          path: ReviewsProductSubPageRoute.Preferences,
          name: ReviewsRouteName.ReviewsProductPreferencesView,
          props: true,
          component: ReviewsProductPreferences,
        },
        {
          path: `${ReviewsProductSubPageRoute.Preferences}/edit`,
          name: ReviewsRouteName.ReviewsProductPreferencesEdit,
          props: true,
          component: ReviewsProductPreferencesEdit,
        },
        {
          path: '',
          name: ReviewsRouteName.ReviewsProductView,
          redirect: { name: ReviewsRouteName.ReviewsProductViewDetails },
          props: true,
          component: ReviewsProductView,
          children: [
            {
              path: ReviewsProductRouteTabs.Details,
              name: ReviewsRouteName.ReviewsProductViewDetails,
              props: true,
              components: { default: ReviewsProductDetails, subheader: ReviewsProductDetailsSubheader },
              meta: {
                tabId: ReviewsProductRouteTabs.Details,
              },
            },
            {
              path: ReviewsProductRouteTabs.GlobalFeatures,
              name: ReviewsRouteName.ReviewsProductViewGlobalFeatures,
              props: true,
              components: { default: ReviewsProductGlobalFeatures, subheader: ReviewsProductGlobalFeaturesSubheader },
              meta: {
                tabId: ReviewsProductRouteTabs.GlobalFeatures,
              },
            },
            {
              path: ReviewsProductRouteTabs.Pricing,
              name: ReviewsRouteName.ReviewsProductViewPricing,
              props: true,
              components: { default: ReviewsProductPricing, subheader: ReviewsProductPricingSubheader },
              meta: {
                tabId: ReviewsProductRouteTabs.Pricing,
              },
            },
            {
              path: ReviewsProductRouteTabs.Assets,
              name: ReviewsRouteName.ReviewsProductViewAssets,
              props: true,
              components: { default: ReviewsProductAssets, subheader: ReviewsProductAssetsSubheader },
              meta: {
                tabId: ReviewsProductRouteTabs.Assets,
              },
            },
          ],
        },
        {
          path: 'edit',
          redirect: { name: ReviewsRouteName.ReviewsProductEditDetails },
          name: ReviewsRouteName.ReviewsProductEdit,
          props: true,
          component: ReviewsProductEdit,
          children: [
            {
              path: ReviewsProductRouteTabs.Details,
              name: ReviewsRouteName.ReviewsProductEditDetails,
              props: true,
              component: ReviewsProductDetailsEdit,
              meta: {
                tabId: ReviewsProductRouteTabs.Details,
              },
            },
            {
              path: ReviewsProductRouteTabs.GlobalFeatures,
              name: ReviewsRouteName.ReviewsProductEditGlobalFeatures,
              props: true,
              component: ReviewsProductGlobalFeaturesEdit,
              meta: {
                tabId: ReviewsProductRouteTabs.GlobalFeatures,
              },
            },
            {
              path: ReviewsProductRouteTabs.Pricing,
              name: ReviewsRouteName.ReviewsProductEditPricing,
              props: true,
              component: ReviewsProductPricingEdit,
              meta: {
                tabId: ReviewsProductRouteTabs.Pricing,
              },
            },
            {
              path: ReviewsProductRouteTabs.Assets,
              name: ReviewsRouteName.ReviewsProductEditAssets,
              props: true,
              component: ReviewsProductAssetsEdit,
              meta: {
                tabId: ReviewsProductRouteTabs.Assets,
              },
            },
          ],
        },
      ],
    },
  ],
}
